/**
 * photoPayloadBudget — FT-577 (2026-11-01). SAFETY-CRITICAL.
 *
 * WHY THIS EXISTS
 * ---------------
 * Every photo (equipment / maintenance / defect evidence AND the vessel profile
 * image) is uploaded as ONE `/sync/push` request whose JSON body carries the
 * full JPEG AND its thumbnail as base64 data-URLs. The server rejects any body
 * whose `JSON.stringify(req.body).length` exceeds its MAX_PAYLOAD_SIZE
 * (sms-pro-api `src/routes/sync.ts`, `validatePayloadSize`). Before FT-577 the
 * client never measured the body, so a detailed photo (≈720 KB+ compressed at
 * the old 1 MB limit) was rejected with 413, retried 3×, then marked failed
 * forever — with NO visible error. Photos were silently and permanently lost.
 *
 * WHAT THIS MODULE PROVIDES (pure — no canvas, no IDB, no network)
 * ----------------------------------------------------------------
 *  - `assemblePhotoCloudRecord`  — the ONE place the cloud record's fields are
 *    laid out (buildPhotoCloudRecord uses it too), so the size measurement can
 *    never drift from what is really sent.
 *  - `computePhotoPushBodyBytes` — the EXACT `/sync/push` body length for a
 *    photo, computed arithmetically from blob sizes (base64 length is
 *    deterministic), wrapped exactly as `outbox.processOutbox` +
 *    `azureApiClient.push` wrap it.
 *  - `computePushBodyBytesForRecord` — same, for an already-built record.
 *  - `encodeWithinBudget` — the shared enforcement loop: re-encode, stepping
 *    JPEG quality DOWN and (when needed) image DIMENSIONS down, until the real
 *    request body fits the budget — or throw `PhotoTooLargeError`.
 *  - `PhotoTooLargeError` — plain-English, user-facing error. Callers MUST show
 *    it; it is never swallowed.
 */

/** Must mirror sms-pro-api `src/routes/sync.ts` MAX_PAYLOAD_SIZE (raised to 2 MB in FT-577). */
export const SERVER_MAX_PUSH_BODY_BYTES = 2 * 1024 * 1024;

/**
 * Client-side budget for a NEW photo's request body. Deliberately well under the
 * server limit (≈400 KB of headroom) so encoder / browser variance, a long
 * caption, or a future envelope field can never tip a photo over the edge.
 */
export const PHOTO_PUSH_BODY_BUDGET_BYTES = Math.floor(1.6 * 1024 * 1024); // 1,677,721

export const PHOTO_COLLECTION = 'sr.photos.v1';

/** Defaults for the enforcement loop. */
export const DEFAULT_QUALITY_FLOOR = 0.5;
export const DEFAULT_MIN_LONG_EDGE = 1024;
export const DEFAULT_MAX_ATTEMPTS = 10;

// A 36-char stand-in used ONLY when a caller cannot supply the real vesselId
// (vessel ids are UUIDs, so this is the same length → same byte count).
const VESSEL_ID_PLACEHOLDER = '00000000-0000-0000-0000-000000000000';

export const PHOTO_TOO_LARGE_MESSAGE =
  'This photo is too large to upload, even after shrinking it, so it has NOT been saved. ' +
  'Please try a different photo, or take it again with a lower camera resolution.';

export class PhotoTooLargeError extends Error {
  /**
   * @param {{ bodyBytes?: number, budgetBytes?: number, message?: string }} [info]
   */
  constructor(info = {}) {
    super(info.message || PHOTO_TOO_LARGE_MESSAGE);
    this.name = 'PhotoTooLargeError';
    this.code = 'PHOTO_TOO_LARGE';
    this.bodyBytes = info.bodyBytes;
    this.budgetBytes = info.budgetBytes;
  }
}

export const isPhotoTooLargeError = (err) =>
  !!err && (err instanceof PhotoTooLargeError || err.code === 'PHOTO_TOO_LARGE');

/**
 * Length of the data-URL `FileReader.readAsDataURL(blob)` produces:
 * `data:<mime>;base64,` + 4·⌈bytes/3⌉. FileReader uses `blob.type`, falling back
 * to application/octet-stream when the type is empty.
 */
export const dataUrlLength = (mime, bytes) =>
  `data:${mime || 'application/octet-stream'};base64,`.length + 4 * Math.ceil((bytes || 0) / 3);

/**
 * Lay out the cloud record (DESIGN-BRIEF §3.3). Single source of truth — used by
 * buildPhotoCloudRecord for the REAL record and by the size measurement below.
 * @param {Object} photoData  photo row (photoId, recordType, recordId, mime, …)
 * @param {{ data: string, thumbData: string, fullBlobType?: string, fullBlobSize?: number }} parts
 * @param {string} vesselId
 */
export const assemblePhotoCloudRecord = (photoData, parts, vesselId) => {
  const record = {
    id: photoData.photoId,
    vesselId,
    recordType: photoData.recordType,
    recordId: photoData.recordId,
    data: parts.data,
    thumbData: parts.thumbData,
    mime: photoData.mime || parts.fullBlobType || 'image/jpeg',
    width: photoData.width || 0,
    height: photoData.height || 0,
    bytes: photoData.bytes || parts.fullBlobSize,
    capturedAt: photoData.capturedAt,
    capturedBy: photoData.capturedBy,
    updatedAt: Date.now(),
    syncedAt: 0,
  };
  if (photoData.caption) record.caption = photoData.caption;
  return record;
};

/**
 * Wrap a record exactly as it travels: outbox.processOutbox spreads it and adds
 * vesselId / updatedAt (ISO string — genTimestamp) / syncedAt (Date.now()), then
 * azureApiClient.push sends `{ collection, id, data, vesselId }`.
 */
const wrapAsPushBody = (record, vesselId) => ({
  collection: PHOTO_COLLECTION,
  id: record.id,
  data: {
    ...record,
    vesselId,
    updatedAt: new Date().toISOString(),
    syncedAt: Date.now(),
  },
  vesselId,
});

/**
 * EXACT length of `JSON.stringify(body)` for an already-built record (what the
 * server's validatePayloadSize measures).
 */
export const computePushBodyBytesForRecord = (record, vesselId) =>
  JSON.stringify(wrapAsPushBody(record, vesselId || record?.vesselId || VESSEL_ID_PLACEHOLDER)).length;

/**
 * EXACT `/sync/push` body length for a photo, WITHOUT base64-encoding anything:
 * the two data-URLs contribute a deterministic length and contain no characters
 * JSON needs to escape, so body = skeleton JSON + both data-URL lengths.
 *
 * @param {{
 *   photoData: Object,          // photoId, recordType, recordId, mime, width, height, bytes, capturedAt, capturedBy, caption?
 *   fullBytes: number, fullMime: string,
 *   thumbBytes: number, thumbMime: string,
 *   vesselId?: string,
 * }} args
 * @returns {number}
 */
export const computePhotoPushBodyBytes = ({ photoData, fullBytes, fullMime, thumbBytes, thumbMime, vesselId }) => {
  const vid = vesselId || VESSEL_ID_PLACEHOLDER;
  const skeleton = assemblePhotoCloudRecord(
    { ...photoData, bytes: photoData.bytes || fullBytes },
    { data: '', thumbData: '', fullBlobType: fullMime, fullBlobSize: fullBytes },
    vid,
  );
  return (
    computePushBodyBytesForRecord(skeleton, vid)
    + dataUrlLength(fullMime, fullBytes)
    + dataUrlLength(thumbMime, thumbBytes)
  );
};

/** Preserve aspect ratio while clamping the LONG edge to `maxLongEdge`. */
export const clampToLongEdge = (srcW, srcH, maxLongEdge) => {
  const long = Math.max(srcW, srcH);
  if (long <= maxLongEdge) return { width: srcW, height: srcH };
  const scale = maxLongEdge / long;
  return { width: Math.max(1, Math.round(srcW * scale)), height: Math.max(1, Math.round(srcH * scale)) };
};

const round2 = (q) => Math.round(q * 100) / 100;

/**
 * Shared enforcement loop (FT-577). Encodes, measures the REAL request body, and
 * — only if it is over budget — steps JPEG quality down AND shrinks dimensions
 * until it fits. Attempt #1 is byte-identical to the pre-FT-577 single-pass
 * encode, so normal-sized photos are completely unchanged.
 *
 * Optional `softTargetBytes` (vessel profile only): while the FULL image is over
 * this size, keep trading quality (never dimensions) down to the floor — the
 * pre-FT-577 profile behaviour. Once at the floor a result over the soft target
 * is accepted IF it is within the hard budget. The hard budget is never waived.
 *
 * @param {{
 *   srcWidth: number, srcHeight: number,
 *   startLongEdge: number, startQuality: number,
 *   qualityFloor?: number, minLongEdge?: number, maxAttempts?: number,
 *   budgetBytes?: number, softTargetBytes?: number,
 *   encode: (width: number, height: number, quality: number) => Promise<{ size: number }>|{ size: number },
 *   measure: (encoded: { size: number }, width: number, height: number) => number,
 * }} opts
 * @returns {Promise<{ encoded: any, width: number, height: number, quality: number, bodyBytes: number, attempts: number }>}
 * @throws {PhotoTooLargeError}
 */
export async function encodeWithinBudget(opts) {
  const {
    srcWidth, srcHeight, startLongEdge, startQuality,
    qualityFloor = DEFAULT_QUALITY_FLOOR,
    minLongEdge = DEFAULT_MIN_LONG_EDGE,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    budgetBytes = PHOTO_PUSH_BODY_BUDGET_BYTES,
    softTargetBytes,
    encode, measure,
  } = opts;
  if (!(srcWidth > 0) || !(srcHeight > 0)) {
    throw new Error('[photoPayloadBudget] encodeWithinBudget: source dimensions required');
  }
  if (typeof encode !== 'function' || typeof measure !== 'function') {
    throw new Error('[photoPayloadBudget] encodeWithinBudget: encode + measure are required');
  }

  const srcLong = Math.max(srcWidth, srcHeight);
  let longEdge = Math.min(startLongEdge, srcLong);
  // Never shrink BELOW the source's own size to reach the floor; a small source
  // just has a smaller effective floor.
  const effectiveMinLong = Math.min(minLongEdge, longEdge);
  let quality = round2(startQuality);
  const floor = Math.min(qualityFloor, quality);
  let lastBody = Infinity;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const { width, height } = clampToLongEdge(srcWidth, srcHeight, longEdge);
    // eslint-disable-next-line no-await-in-loop -- sequential by design (each step depends on the last)
    const encoded = await encode(width, height, quality);
    const bodyBytes = measure(encoded, width, height);
    lastBody = bodyBytes;

    const fitsHard = bodyBytes <= budgetBytes;
    const atFloor = quality <= floor + 1e-9;
    const softOk = !softTargetBytes || encoded.size <= softTargetBytes || atFloor;
    if (fitsHard && softOk) {
      return { encoded, width, height, quality, bodyBytes, attempts: attempt };
    }

    if (fitsHard) {
      // Only the SOFT (profile) target is unmet: trade quality, hold resolution.
      quality = round2(Math.max(floor, quality - 0.1));
      continue;
    }

    // Over the HARD budget. How far over? (portion of the body the full image owns)
    const fullB64 = 4 * Math.ceil(encoded.size / 3);
    const fixed = Math.max(0, bodyBytes - fullB64);
    const ratio = Math.max(0.01, (budgetBytes - fixed) / Math.max(1, bodyBytes - fixed));
    const canShrink = longEdge > effectiveMinLong;

    if (!atFloor) quality = round2(Math.max(floor, quality - 0.1));
    // Shrink dimensions when quality alone can't do it: already at the floor,
    // or far over budget (>30 % too big) — reduce BOTH in one step.
    if ((atFloor || ratio < 0.7) && canShrink) {
      const scale = Math.min(0.9, Math.max(0.5, Math.sqrt(ratio) * 0.95));
      longEdge = Math.max(effectiveMinLong, Math.floor(longEdge * scale));
    } else if (atFloor && !canShrink) {
      break; // nothing left to reduce
    }
  }

  throw new PhotoTooLargeError({ bodyBytes: lastBody, budgetBytes });
}
